import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  GlobalStyles,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import Link from "@mui/material/Link";
import { useRecoilValue } from "recoil";
import { useGetAccountStatementReportData } from "src/api/reports";
import { useGetBranches } from "src/api/generic";
import DateRangeInput from "src/shared/components/date-range";
import {
  ReportControlBar,
  ReportDataTable,
  ReportPageHeading,
  ReportSectionCard,
  type ReportTableColumn,
} from "src/shared/components/report-layout";
import { authState } from "src/store/auth";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { useGetTreeAccountsList } from "src/api/tree/tree";

type DetailRow = {
  purchase_id: number;
  purchase_ref: string;
  branch_name: string;
  due_date: string;
  item_name: string;
  unit_name: string;
  quantity: number;
  net_total: number;
  fluctuation_percent: number;
};

type ReportFilters = {
  startDate: string;
  endDate: string;
  branchId: string;
  itemId: string;
};

const ExpensesStatementReportPage = () => {
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const { t: purchasingT } = useTranslation("purchasing");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useRecoilValue(authState);
  const companyName = auth.user?.company?.name;

  const formValues = useMemo((): ReportFilters => {
    return {
      startDate:
        searchParams.get("start_date") ||
        moment().startOf("month").format("yyyy-MM-DD"),
      endDate:
        searchParams.get("end_date") ||
        moment().endOf("month").format("yyyy-MM-DD"),
      branchId: searchParams.get("branch_id") || "",
      itemId: searchParams.get("item_id") || "",
    };
  }, [searchParams]);

  const { control, setValue } = useForm<ReportFilters>({
    values: formValues,
  });

  const {
    startDate,
    endDate,
    branchId: branchIdParam,
    itemId: itemIdParam,
  } = formValues;

  const { data: branchesData } = useGetBranches();
  const { data: accountsData } = useGetTreeAccountsList({category_system_key: "expenses"});

  const branchesHavePermission = branchesData?.data || [];

  const sortedAccounts = useMemo(() => {
    return [...(accountsData?.data || [])].sort((a: any, b: any) =>
      a.name?.localeCompare(b.name),
    );
  }, [accountsData]);

  const itemIdValid = itemIdParam.length > 0;
  const branchIdNum = branchIdParam ? Number(branchIdParam) : NaN;
  const branchFilter = Number.isFinite(branchIdNum) ? [branchIdNum] : [];

  const {
    data: reportResponse,
    isFetching,
    refetch: refetchReport,
  } = useGetAccountStatementReportData({
    branches: branchFilter,
    start_date: startDate,
    end_date: endDate,
    chart_account_id: itemIdParam,
    enabled: false,
  });

  const rows = useMemo(() => reportResponse?.data ?? [], [reportResponse]);

  const totalDebit = useMemo(
    () =>
      rows.reduce((sum: number, row: any) => {
        const val = parseFloat(row.debit) || 0;
        return sum + val;
      }, 0),
    [rows],
  );

  const totalCredit = useMemo(
    () =>
      rows.reduce((sum: number, row: any) => {
        const val = parseFloat(row.credit) || 0;
        return sum + val;
      }, 0),
    [rows],
  );

  const syncDatesToUrl = (start: string, end: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("start_date", start);
    next.set("end_date", end);
    setSearchParams(next);
  };

  const syncItemToUrl = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("item_id", id);
    else next.delete("item_id");
    setSearchParams(next);
  };

  const syncBranchToUrl = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("branch_id", id);
    else next.delete("branch_id");
    setSearchParams(next);
  };

  const periodLabel = useMemo(() => {
    return t("reportDateRange", {
      start: moment(startDate).format("D MMM YYYY"),
      end: moment(endDate).format("D MMM YYYY"),
    });
  }, [t, startDate, endDate]);

  const handlePrint = () => {
    window.print();
  };

  const columns: ReportTableColumn<any>[] = useMemo(
    () => [
      {
        id: "date",
        header: generalT("date"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.date}
          </Typography>
        ),
      },
      {
        id: "journalId",
        header: purchasingT("journalId"),
        cell: (row) => {
          if (!row.reference) return null;
          return (
            <Link
              component={RouterLink}
              to={`/for-accountant/ledger?reference=${encodeURIComponent(row.reference)}`}
              underline="always"
              sx={{ fontSize: "inherit" }}
            >
              {row.reference}
            </Link>
          );
        },
      },
      {
        id: "source",
        header: purchasingT("source"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.source_text}
          </Typography>
        ),
      },
      {
        id: "reference",
        header: generalT("reference"),
        cell: (row) => {
          if (!row.related_reference) return null;
          const refParam = encodeURIComponent(row.related_reference);
          const sourceRouteMap: Record<string, string> = {
            purchasing: `/purchasing/list?reference=${refParam}`,
            event: `/events/list?reference=${refParam}`,
            manual_journal: `/for-accountant/manual-journals?reference=${refParam}`,
            sales: `/sales/list?reference=${refParam}`,
          };
          const to =
            sourceRouteMap[row.source] ??
            `/for-accountant/ledger?reference=${refParam}`;
          return (
            <Link
              component={RouterLink}
              to={to}
              underline="always"
              sx={{ fontSize: "inherit" }}
            >
              {row.related_reference}
            </Link>
          );
        },
      },
      {
        id: "account",
        header: generalT("account"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.account}
          </Typography>
        ),
      },
      {
        id: "parentAccount",
        header: purchasingT("parentAccount"),
        align: "right",
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.parent_account}
          </Typography>
        ),
      },
      {
        id: "description",
        header: generalT("description"),
        align: "right",
        cell: (row) => (
          <Box component="span" fontWeight={600}>
            {row.description}
          </Box>
        ),
      },
      {
        id: "debit",
        header: generalT("debit"),
        align: "right",
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.debit}
          </Typography>
        ),
      },
      {
        id: "credit",
        header: generalT("credit"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.credit}
          </Typography>
        ),
      },
      {
        id: "branch",
        header: generalT("branch"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.branch}
          </Typography>
        ),
      },
      {
        id: "createdAt",
        header: generalT("createdAt"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.created_at}
          </Typography>
        ),
      },
      {
        id: "createdBy",
        header: generalT("createdBy"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.created_by?.name}
          </Typography>
        ),
      },
    ],
    [navigate],
  );

  return (
    <Box p={{ xs: 2, sm: "12px 32px 32px" }} maxWidth="100%">
      <GlobalStyles
        styles={{
          "@media print": {
            "body *": { visibility: "hidden" },
            "#item-detail-print-root, #item-detail-print-root *": {
              visibility: "visible",
            },
            "#item-detail-print-root": {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
            },
          },
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        color="#101828"
        mb={1.5}
        sx={{ "@media print": { display: "none" } }}
      >
        <Typography
          fontSize="0.9rem"
          color="#475467"
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" style={{ width: 12, height: 12 }} />
        <Box borderRadius="4px" bgcolor="#EAECF0" px={1} py={0.5}>
          <Typography color="#344054" fontWeight={600} fontSize="0.85rem">
            {t("expensesStatementReport")}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ "@media print": { display: "none" } }} mb={2}>
        <ReportControlBar
          left={
            <>
              <Controller
                name="branchId"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="expenses-statement-branch" shrink>
                      {generalT("branch")}
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="expenses-statement-branch"
                      label={generalT("branch")}
                      onChange={(e) => {
                        const v = e.target.value as string;
                        field.onChange(v);
                        syncBranchToUrl(v);
                      }}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>{t("allBranches")}</em>
                      </MenuItem>
                      {branchesHavePermission.map((branch) => (
                        <MenuItem key={branch.id} value={String(branch.id)}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="itemId"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 220 }}
                    options={sortedAccounts}
                    getOptionLabel={(option: { id: number; name: string }) =>
                      option.name ?? ""
                    }
                    value={
                      sortedAccounts.find(
                        (a: { id: number }) =>
                          String(a.id) === String(field.value),
                      ) ?? null
                    }
                    onChange={(_e, newValue: { id: number; name: string } | null) => {
                      const v = newValue ? String(newValue.id) : "";
                      field.onChange(v);
                      syncItemToUrl(v);
                    }}
                    isOptionEqualToValue={(option, val) =>
                      String(option.id) === String(val.id)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("expenseAccount")}
                        InputLabelProps={{ shrink: true }}
                        placeholder={t("selectItem")}
                      />
                    )}
                  />
                )}
              />

              <Box sx={{ minWidth: 250 }}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DateRangeInput
                      startDate={field.value ? moment(field.value) : null}
                      startDateId="expenses-statement-start"
                      endDate={
                        formValues.endDate ? moment(formValues.endDate) : null
                      }
                      endDateId="expenses-statement-end"
                      onDatesChange={(arg: {
                        startDate: moment.Moment | null;
                        endDate: moment.Moment | null;
                      }) => {
                        const s = arg.startDate?.format("yyyy-MM-DD");
                        const e = arg.endDate?.format("yyyy-MM-DD");
                        if (s && e) {
                          field.onChange(s);
                          setValue("endDate", e);
                          syncDatesToUrl(s, e);
                        }
                      }}
                    />
                  )}
                />
              </Box>
            </>
          }
          center={
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={() => void refetchReport()}
                disabled={!itemIdValid || isFetching}
                sx={{ height: 40, minWidth: 120 }}
              >
                {t("reportUpdate")}
              </Button>
            </>
          }
          right={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PrintIcon fontSize="small" />}
                onClick={handlePrint}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("print")}
              </Button>
            </>
          }
        />
      </Box>

      <Box id="item-detail-print-root">
        <ReportPageHeading
          title={t("expensesStatementReport")}
          subtitle={companyName}
          periodLabel={periodLabel}
        />

        <ReportSectionCard>
          {!itemIdValid ? (
            <Box p={4} textAlign="center">
              <Typography color="text.secondary">
                {t("selectAccountToViewReport")}
              </Typography>
            </Box>
          ) : (
            <ReportDataTable<DetailRow>
              columns={columns}
              rows={rows}
              getRowId={(row, i) => `${row.purchase_ref}-${i}`}
              isLoading={isFetching}
              emptyMessage={
                <Typography color="text.secondary" align="center" py={2}>
                  {generalT("noData")}
                </Typography>
              }
              footer={
                rows.length > 0 ? (
                  <TableRow
                    sx={{
                      "& td": {
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        borderTop: "2px solid #EAECF0",
                        bgcolor: "#F9FAFB",
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell colSpan={7}>
                      <Typography fontWeight={700}>
                        {generalT("total")}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} dir="auto">
                        {totalDebit.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={700} dir="auto">
                        {totalCredit.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                ) : undefined
              }
            />
          )}
        </ReportSectionCard>
      </Box>
    </Box>
  );
};

export default ExpensesStatementReportPage;
